import { Platform } from '@cart-services/hooks/fp-b2b/store-map'
import { type CountryCode } from '@constants'
import { useStmAnalyticsEvent } from '@dni-analysis'
import { StmSectionName } from '@dni-analysis/types/fastpass-ga4-events'
import { useDialog } from '@dni-hooks/useDialog'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { type CrossBrandMallHomeCampaign } from '@hooks-api/types/dataModel/cross-brand-mall'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Text } from '@pets-ui/Text/Text'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useState } from 'react'
import { useHistory } from 'react-router-dom-v5'
import { FpB2bProductCatalogProvider } from '../../../fp-b2b-shared/components/ProductCatalogProvider/ProductCatalogProvider'
import { DIALOG_PRODUCT_CART, DialogProductCart, type DialogProductCartProps } from '../../components/DialogProductCart'
import { useStmBrandStore } from '../../hooks'
import { useBrandPromoFilteredProducts } from '../hooks/use-brand-promo-filtered-products'
import { type ProductCardEntry } from '../types/product-card-entry'
import { BrandPromoCouponList } from './BrandPromoCouponList'
import { NavigateToBrandCard } from './NavigateToBrandCard'
import { ProductCard } from './ProductCard'
import { ProductList } from './ProductList'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const styles = stylex.create({
  root: {
    flexBasis: 0,
    flexDirection: { [MOBILE]: 'column', default: 'row' },
    flexGrow: 1,
    flexShrink: 1,
    flexWrap: 'nowrap',
    minWidth: 0,
  },
  couponPanel: {
    borderColor: colors.grey200,
    borderStyle: 'solid',
    flexShrink: 0,
    borderBottomWidth: { [MOBILE]: 1, default: 0 },
    borderRightWidth: { [MOBILE]: 0, default: 1 },
    width: { [MOBILE]: '100%', default: 300 },
  },
  productPanel: {
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    width: { [MOBILE]: '100%', default: 'auto' },
  },
})

interface BrandPromoContentProps {
  campaign: CrossBrandMallHomeCampaign
  countryCode: CountryCode
  currency: string
  basePath: string
}

export const BrandPromoContentSkeleton = () => (
  <HStack rootStyle={styles.root}>
    <Box p={3} rootStyle={styles.couponPanel}>
      <VStack gap={2}>
        <Text color={colors.grey600}>放大你的利潤空間！進貨折扣這裡看</Text>
        <BrandPromoCouponList
          coupons={[]}
          selectedCouponId={null}
          onSelect={() => undefined}
          isLoading={true}
        />
      </VStack>
    </Box>
    <Box p={3} rootStyle={styles.productPanel}>
      <ProductList isLoading={true} />
    </Box>
  </HStack>
)

export const BrandPromoContent = (props: BrandPromoContentProps) => {
  const { campaign, countryCode, currency, basePath } = props

  const [userSelectedCouponId, setUserSelectedCouponId] = useState<number | null>(null)

  const {
    highlightCoupons,
    selectedCouponId,
    productCardEntries,
    isDisplayLimited,
    isCouponsLoading,
    isProductsLoading,
  } = useBrandPromoFilteredProducts({ campaign, countryCode, userSelectedCouponId })

  const brandInfo = useStmBrandStore(state =>
    state.brands.find(brand => brand.campaignCode === campaign.code),
  )
  const dialogKey = `${DIALOG_PRODUCT_CART}_${campaign.code}`
  const { openDialog } = useDialog<DialogProductCartProps>(dialogKey)
  const { setBrand } = useStmBrandStore()
  const history = useHistory()
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaign.code })
  const { sendCrossBrandAddToCartEvent } = useStmAnalyticsEvent({ userId: userInfo?.platform_uid ?? '' })

  if (!brandInfo) return null

  const handleOpenCartDialog = (entry: ProductCardEntry) => {
    sendCrossBrandAddToCartEvent({ projectCode: campaign.project_code, sectionName: StmSectionName.BRAND_PROMO })
    openDialog({
      title: entry.title,
      thumbnail: entry.imageUrl,
      variations: entry.variations,
      basePath,
    })
  }

  const handleNavigateToBrand = () => {
    setBrand(brandInfo.campaignCode)
    history.push(`${basePath}/product-catalog`)
  }

  return (
    <FpB2bProductCatalogProvider
      platform={Platform.STM}
      campaignId={brandInfo.campaignId}
      campaignCode={brandInfo.campaignCode}
      projectCode={brandInfo.projectCode}
      countryCode={brandInfo.countryCode}
      currency={currency}
    >
      <HStack rootStyle={styles.root}>
        <Box p={3} rootStyle={styles.couponPanel}>
          <VStack gap={2}>
            <Text color={colors.grey600}>放大你的利潤空間！進貨折扣這裡看</Text>
            <BrandPromoCouponList
              coupons={highlightCoupons}
              selectedCouponId={selectedCouponId}
              onSelect={setUserSelectedCouponId}
              isLoading={isCouponsLoading}
            />
          </VStack>
        </Box>
        {(isProductsLoading || productCardEntries.length > 0) && (
          <Box p={3} rootStyle={styles.productPanel}>
            <ProductList
              isLoading={isProductsLoading}
              trailingSlot={isDisplayLimited ? <NavigateToBrandCard onClick={handleNavigateToBrand} /> : undefined}
            >
              {productCardEntries.map(entry => (
                <ProductCard key={entry.key} entry={entry} onSelect={handleOpenCartDialog} />
              ))}
            </ProductList>
          </Box>
        )}
      </HStack>
      <DialogProductCart dialogKey={dialogKey} />
    </FpB2bProductCatalogProvider>
  )
}
